import { mapValues } from 'lodash-es';
import type { BulkError } from '~api';

export class ExtendedError extends Error {
  response?: any;
  rawResponse?: any;
  requestInfo?: any;
  reason?: 'timeout' | 'server-error' | 'unauthorized';
  statusCode?: number;
}

export const getApiValidationErrors = (error: any) => {
  const errors = error.response?.errors;
  if (typeof errors === 'object') {
    return mapValues(errors, value => {
      return String(value);
    });
  }
  return null;
};

const convertCommonErrorsToHumanText = (message: string): string => {
  // Typical 404 or when backend responds with HTML instead of JSON
  // Catches the typical Unexpected token < in JSON at position 0
  if (message.includes('Unexpected token')) {
    return 'Sorry, server responded with faulty data. Please try reloading';
  }
  // Catch BOM-related errors and our custom JSON parsing error
  if (
    message.includes('Unexpected non-whitespace character') ||
    message.startsWith('Server responded with invalid JSON')
  ) {
    return 'Sorry, server responded with faulty data. Please try reloading';
  }
  return message;
};

// Turns API errors into human readable format
export const errorMessagesFactory = (errorPayload: Partial<ExtendedError>): Array<string> => {
  let errors: Record<string, string> = {};

  if (errorPayload?.reason === 'timeout') {
    return ['The request timed out'];
  }

  const response = errorPayload && errorPayload.response;
  if (response) {
    if (response.errors) {
      errors = response.errors;
    } else if (response.error_description) {
      errors = { Error: response.error_description };
    } else if (response.error) {
      errors = { Error: response.error };
    } else if (response.message) {
      // Rails 422 envelope is `{ message }`; for `timelyApi` (a CUSTOM baseQuery,
      // not fetchBaseQuery) the body lands at `error.response.message`. Surface it
      // so server-authoritative reasons (e.g. "The day is locked for this user")
      // reach the toast instead of a hardcoded generic. (`error.data.message` is
      // the TIC `ticApi` fetchBaseQuery shape — different code path.)
      errors = { Error: response.message };
    } else if (typeof response === 'string') {
      errors = { Error: response };
    }
  }

  const errorKeys = Object.keys(errors);

  // Dealing with new Error('whops!') if we're not supplied
  // a rails-style error object.
  if (!errorKeys.length && errorPayload.message) {
    const message = convertCommonErrorsToHumanText(errorPayload.message);
    return [message];
  }

  const readableErrors = errorKeys.map(errorKey => {
    const errorMessage = extractErrorMessage(errors[errorKey]);
    if (
      errorKey === 'message' ||
      errorKey === 'Error' ||
      errorMessage.toLowerCase().startsWith(`${errorKey} `) ||
      (errorKey === '0' && errorKeys.length === 1)
    ) {
      return errorMessage;
    }
    return `${errorKey} ${errorMessage}`;
  });

  return readableErrors.map(error => {
    return error.charAt(0).toUpperCase() + error.slice(1);
  });
};

export const errorMessageFactory = (errorPayload: Partial<ExtendedError>): string => {
  return errorMessagesFactory(errorPayload).join(', ');
};

/**
 * Extract a human-readable error message from bulk API response errors.
 * The bulk hours API returns errors in the format: { index: number, messages: { field: string[] } }
 * This differs from the ExtendedError format used by errorMessageFactory.
 */
export const extractBulkErrorMessage = (errors: BulkError[]): string => {
  if (!errors.length) return 'Failed to save hours';

  // Get unique error messages from all errors
  const uniqueMessages = new Set<string>();
  for (const error of errors) {
    if (error.messages && typeof error.messages === 'object') {
      for (const fieldMessages of Object.values(error.messages)) {
        if (Array.isArray(fieldMessages)) {
          fieldMessages.forEach(msg => uniqueMessages.add(msg));
        }
      }
    }
  }

  // Return first unique message, or fallback
  return uniqueMessages.size > 0 ? Array.from(uniqueMessages)[0] : 'Failed to save hours';
};

const extractErrorMessage = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(extractErrorMessage).join(', ');
  } else if (Object.prototype.toString.call(value) === '[object Object]') {
    return 'Something went wrong!';
  }
  return String(value);
};
